
import { useState, useEffect, useCallback, useMemo, type KeyboardEvent } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { SlideDrawer } from '@/components/ui/slide-drawer';
import {
    X,
    Package,
    Loader2,
    ChevronRight,
    ChevronLeft,
    Check,
    AlertCircle,
    Search,
    DollarSign,
    Zap,
    Bot,
    GitBranch,
    Wrench,
    Sparkles,
    Trash2,
    Plus,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Switch } from '@/components/ui/switch';
import { Skeleton } from '@/components/ui/skeleton';
import { Progress } from '@/components/ui/progress';
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from '@/components/ui/select';
import { api } from '@/lib/api-client';
import { useAuth } from '@/lib/auth';
import { logger } from '@/lib/telemetry';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ProductFormWizardProps {
    isOpen: boolean;
    onClose: () => void;
    onProductCreated?: () => void;
    preselectedCapabilityId?: string;
}

interface WizardStep {
    id: string;
    title: string;
    description: string;
}

interface ApplicationOption {
    id: string;
    app_key: string;
    name: string | null;
    description?: string | null;
    display_name?: string;
}

interface CapabilityOption {
    id: string;
    name: string;
    fqcn?: string;
    type: string;
    description: string | null;
    visibility?: string;
}

type PricingModel = 'flat' | 'metered' | 'per_unit' | 'tiered';
type BillingInterval = 'month' | 'year';
type MetricType = 'invocations' | 'tokens' | 'storage_bytes' | 'compute_seconds' | 'messages';

interface TierEntry {
    up_to: number | null;
    unit_amount_cents: number;
}

interface FeaturePriceConfig {
    capability_id: string;
    name: string;
    pricing_model: PricingModel;
    amount_cents: number;
    billing_interval: BillingInterval;
    metric: MetricType;
    usage_limit: number | null;
    overage_rate_cents: number;
    tiers: TierEntry[];
}

interface BasePriceConfig {
    amount_cents: number;
    currency: string;
    billing_interval: BillingInterval;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const WIZARD_STEPS: WizardStep[] = [
    { id: 'application', title: 'Select Application', description: 'Choose an app and name your product' },
    { id: 'capabilities', title: 'Select Capabilities', description: 'Pick capabilities to include' },
    { id: 'pricing', title: 'Configure Pricing', description: 'Set up pricing for your product' },
];

const PRICING_MODELS: { value: PricingModel; label: string; description: string }[] = [
    { value: 'flat', label: 'Flat', description: 'Fixed recurring price' },
    { value: 'metered', label: 'Metered', description: 'Pay per usage' },
    { value: 'per_unit', label: 'Per Unit', description: 'Price per unit per interval' },
    { value: 'tiered', label: 'Tiered', description: 'Volume-based pricing tiers' },
];

const METRIC_OPTIONS: { value: MetricType; label: string }[] = [
    { value: 'invocations', label: 'Invocations' },
    { value: 'tokens', label: 'Tokens' },
    { value: 'storage_bytes', label: 'Storage (bytes)' },
    { value: 'compute_seconds', label: 'Compute (seconds)' },
    { value: 'messages', label: 'Messages' },
];

const CAPABILITY_TYPE_GROUPS = ['tool', 'agent', 'workflow'] as const;
const PRODUCT_CAP_PAGE_SIZE = 500;

const TYPE_ICONS: Record<string, typeof Wrench> = {
    tool: Wrench,
    agent: Bot,
    workflow: GitBranch,
};

const TYPE_COLORS: Record<string, string> = {
    tool: 'bg-amber-100 text-amber-700',
    agent: 'bg-blue-100 text-blue-700',
    workflow: 'bg-violet-100 text-violet-700',
};

const APPLICATION_NONE = '__none__';

const inputClassName =
    'w-full rounded-md border border-slate-200 bg-white py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary';
const errorInputClassName =
    'w-full rounded-md border border-red-300 bg-red-50/50 py-2 px-3 text-sm font-medium text-slate-700 placeholder-slate-400 focus:border-red-500 focus:outline-none focus:ring-1 focus:ring-red-500';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function buildDefaultFeaturePrice(cap: CapabilityOption): FeaturePriceConfig {
    return {
        capability_id: cap.id,
        name: cap.name,
        pricing_model: 'flat',
        amount_cents: 0,
        billing_interval: 'month',
        metric: 'invocations',
        usage_limit: null,
        overage_rate_cents: 0,
        tiers: [{ up_to: 100, unit_amount_cents: 100 }, { up_to: null, unit_amount_cents: 50 }],
    };
}

function centsToDisplay(cents: number): string {
    return (cents / 100).toFixed(2);
}

function displayToCents(display: string): number {
    const parsed = parseFloat(display);
    return Number.isNaN(parsed) ? 0 : Math.round(parsed * 100);
}

function parseCapabilityListResponse(data: unknown): CapabilityOption[] {
    const rec = data as { data?: unknown; capabilities?: unknown } | unknown[] | null | undefined;
    const raw = Array.isArray(rec)
        ? rec
        : rec?.data ?? rec?.capabilities ?? [];
    if (!Array.isArray(raw)) return [];
    return raw.filter((c): c is CapabilityOption => {
        if (!c || typeof c !== 'object') return false;
        const cap = c as CapabilityOption;
        return typeof cap.name === 'string' && typeof cap.type === 'string';
    });
}

function dedupeCapabilities(caps: CapabilityOption[]): CapabilityOption[] {
    const seen = new Set<string>();
    const out: CapabilityOption[] = [];
    for (const cap of caps) {
        const key = cap.id || cap.fqcn || `${cap.type}:${cap.name}`;
        if (seen.has(key)) continue;
        seen.add(key);
        out.push(cap);
    }
    return out;
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function ProductFormWizard({
    isOpen,
    onClose,
    onProductCreated,
    preselectedCapabilityId,
}: ProductFormWizardProps) {
    const { identity, currentOrganization } = useAuth();

    // Wizard state
    const [currentStep, setCurrentStep] = useState(0);
    const [publishing, setPublishing] = useState(false);
    const [errors, setErrors] = useState<Record<string, string>>({});

    // Step 1 — Application
    const [applications, setApplications] = useState<ApplicationOption[]>([]);
    const [loadingApps, setLoadingApps] = useState(false);
    const [selectedAppKey, setSelectedAppKey] = useState('');
    const [selectedAppId, setSelectedAppId] = useState('');
    const [title, setTitle] = useState('');
    const [summary, setSummary] = useState('');

    // Step 2 — Capabilities
    const [capabilities, setCapabilities] = useState<CapabilityOption[]>([]);
    const [loadingCaps, setLoadingCaps] = useState(false);
    const [selectedCapIds, setSelectedCapIds] = useState<Set<string>>(new Set());
    const [capSearch, setCapSearch] = useState('');

    // Step 3 — Pricing
    const [trialDays, setTrialDays] = useState(0);
    const [enableBasePrice, setEnableBasePrice] = useState(false);
    const [basePrice, setBasePrice] = useState<BasePriceConfig>({
        amount_cents: 0,
        currency: 'usd',
        billing_interval: 'month',
    });
    const [featurePrices, setFeaturePrices] = useState<FeaturePriceConfig[]>([]);

    // -----------------------------------------------------------------------
    // Reset on open
    // -----------------------------------------------------------------------
    useEffect(() => {
        if (isOpen) {
            setCurrentStep(0);
            setErrors({});
            setPublishing(false);
            setSelectedAppKey('');
            setSelectedAppId('');
            setTitle('');
            setSummary('');
            setCapabilities([]);
            setSelectedCapIds(new Set());
            setCapSearch('');
            setTrialDays(0);
            setEnableBasePrice(false);
            setBasePrice({ amount_cents: 0, currency: 'usd', billing_interval: 'month' });
            setFeaturePrices([]);
            fetchApplications();
        }
    }, [isOpen]);

    // -----------------------------------------------------------------------
    // Data fetching
    // -----------------------------------------------------------------------
    const fetchApplications = async () => {
        setLoadingApps(true);
        try {
            const res = await api.get('/applications', { params: { page: 1, per_page: 100 } });
            const apps: ApplicationOption[] = res.data?.applications ?? res.data?.data ?? (Array.isArray(res.data) ? res.data : []);
            setApplications(apps);
        } catch (err) {
            logger.error('ProductFormWizard:fetchApplications - Failed to fetch applications', { error: err });
            setApplications([]);
        } finally {
            setLoadingApps(false);
        }
    };

    const fetchCapabilities = useCallback(async (appKey: string) => {
        setLoadingCaps(true);
        try {
            const results = await Promise.all(
                CAPABILITY_TYPE_GROUPS.map((type) =>
                    api.get(`/applications/${appKey}/capabilities`, {
                        params: { type, per_page: PRODUCT_CAP_PAGE_SIZE, status: 'active' },
                    }),
                ),
            );
            const caps = dedupeCapabilities(
                results.flatMap((res) => parseCapabilityListResponse(res.data)),
            );
            setCapabilities(caps);

            if (preselectedCapabilityId) {
                setSelectedCapIds(new Set([preselectedCapabilityId]));
            }
        } catch (err) {
            logger.error('ProductFormWizard:fetchCapabilities - Failed to fetch capabilities', { error: err });
            setCapabilities([]);
        } finally {
            setLoadingCaps(false);
        }
    }, [preselectedCapabilityId]);

    // Fetch capabilities when app changes
    useEffect(() => {
        if (selectedAppKey) {
            fetchCapabilities(selectedAppKey);
        } else {
            setCapabilities([]);
            setSelectedCapIds(new Set());
        }
    }, [selectedAppKey, fetchCapabilities]);

    // Sync feature prices when selected capabilities change
    useEffect(() => {
        setFeaturePrices((prev) => {
            const existingById = new Map(prev.map((fp) => [fp.capability_id, fp]));
            const selectedCaps = capabilities.filter((c) => selectedCapIds.has(c.id));
            return selectedCaps.map((cap) => existingById.get(cap.id) ?? buildDefaultFeaturePrice(cap));
        });
    }, [selectedCapIds, capabilities]);

    // -----------------------------------------------------------------------
    // Filtered / grouped capabilities
    // -----------------------------------------------------------------------
    const filteredCapabilities = useMemo(() => {
        const q = capSearch.toLowerCase().trim();
        if (!q) return capabilities;
        return capabilities.filter(
            (c) =>
                c.name.toLowerCase().includes(q) ||
                (c.description?.toLowerCase().includes(q) ?? false) ||
                c.type.toLowerCase().includes(q),
        );
    }, [capabilities, capSearch]);

    const groupedCapabilities = useMemo(() => {
        const groups: Record<string, CapabilityOption[]> = {};
        for (const cap of filteredCapabilities) {
            const key = CAPABILITY_TYPE_GROUPS.includes(cap.type as (typeof CAPABILITY_TYPE_GROUPS)[number])
                ? cap.type
                : 'other';
            (groups[key] ??= []).push(cap);
        }
        return groups;
    }, [filteredCapabilities]);

    const groupOrder = [...CAPABILITY_TYPE_GROUPS.filter((t) => groupedCapabilities[t]), ...(groupedCapabilities['other'] ? ['other'] : [])] as string[];

    // -----------------------------------------------------------------------
    // Validation
    // -----------------------------------------------------------------------
    const validateStep = (step: number): boolean => {
        const newErrors: Record<string, string> = {};

        if (step === 0) {
            if (!selectedAppKey) newErrors.application = 'Please select an application';
            if (!title.trim()) newErrors.title = 'Product title is required';
        }

        if (step === 1) {
            if (selectedCapIds.size === 0) newErrors.capabilities = 'Select at least one capability';
        }

        if (step === 2) {
            for (const fp of featurePrices) {
                if (fp.pricing_model === 'metered' && fp.amount_cents <= 0) {
                    newErrors[`feature_${fp.capability_id}`] = 'Metered pricing requires a unit amount greater than 0';
                    break;
                }
                if (fp.pricing_model === 'tiered' && fp.tiers.length < 1) {
                    newErrors[`feature_${fp.capability_id}`] = 'Tiered pricing requires at least one tier';
                    break;
                }
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const canAdvance = (step: number): boolean => {
        if (step === 0) return !!selectedAppKey && !!title.trim();
        if (step === 1) return selectedCapIds.size > 0;
        return true;
    };

    // -----------------------------------------------------------------------
    // Navigation
    // -----------------------------------------------------------------------
    const handleNext = () => {
        if (validateStep(currentStep)) {
            setCurrentStep((prev) => Math.min(prev + 1, WIZARD_STEPS.length - 1));
        }
    };

    const handleBack = () => {
        setErrors({});
        setCurrentStep((prev) => Math.max(prev - 1, 0));
    };

    const handleKeyDown = (e: KeyboardEvent<HTMLDivElement>) => {
        if (e.key === 'Enter' && !e.shiftKey && canAdvance(currentStep)) {
            e.preventDefault();
            if (currentStep < WIZARD_STEPS.length - 1) {
                handleNext();
            } else {
                handlePublish();
            }
        }
    };

    // -----------------------------------------------------------------------
    // Application selection handler
    // -----------------------------------------------------------------------
    const handleAppChange = (value: string) => {
        if (value === APPLICATION_NONE) {
            setSelectedAppKey('');
            setSelectedAppId('');
            return;
        }
        const app = applications.find((a) => a.app_key === value);
        if (app) {
            setSelectedAppKey(app.app_key);
            setSelectedAppId(app.id);
        }
    };

    // -----------------------------------------------------------------------
    // Capability selection helpers
    // -----------------------------------------------------------------------
    const toggleCapability = (id: string) => {
        setSelectedCapIds((prev) => {
            const next = new Set(prev);
            if (next.has(id)) next.delete(id);
            else next.add(id);
            return next;
        });
    };

    const selectAllVisible = () => {
        setSelectedCapIds((prev) => {
            const next = new Set(prev);
            for (const cap of filteredCapabilities) {
                if (cap.id) next.add(cap.id);
            }
            return next;
        });
    };

    const clearSelection = () => setSelectedCapIds(new Set());

    // -----------------------------------------------------------------------
    // Feature price mutation helpers
    // -----------------------------------------------------------------------
    const updateFeaturePrice = (capId: string, patch: Partial<FeaturePriceConfig>) => {
        setFeaturePrices((prev) =>
            prev.map((fp) => (fp.capability_id === capId ? { ...fp, ...patch } : fp)),
        );
    };

    const addTier = (capId: string) => {
        setFeaturePrices((prev) =>
            prev.map((fp) => {
                if (fp.capability_id !== capId) return fp;
                return { ...fp, tiers: [...fp.tiers, { up_to: null, unit_amount_cents: 0 }] };
            }),
        );
    };

    const removeTier = (capId: string, idx: number) => {
        setFeaturePrices((prev) =>
            prev.map((fp) => {
                if (fp.capability_id !== capId) return fp;
                return { ...fp, tiers: fp.tiers.filter((_, i) => i !== idx) };
            }),
        );
    };

    const updateTier = (capId: string, idx: number, patch: Partial<TierEntry>) => {
        setFeaturePrices((prev) =>
            prev.map((fp) => {
                if (fp.capability_id !== capId) return fp;
                return {
                    ...fp,
                    tiers: fp.tiers.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
                };
            }),
        );
    };

    // -----------------------------------------------------------------------
    // Publish
    // -----------------------------------------------------------------------
    const handlePublish = async () => {
        if (!validateStep(currentStep)) return;

        setPublishing(true);
        setErrors({});

        const payload = {
            application_id: selectedAppId,
            organization_id: currentOrganization?.id ?? '',
            title: title.trim(),
            summary: summary.trim() || undefined,
            trial_days: trialDays,
            capability_ids: Array.from(selectedCapIds),
            base_price: enableBasePrice
                ? { amount_cents: basePrice.amount_cents, currency: basePrice.currency, billing_interval: basePrice.billing_interval }
                : undefined,
            feature_prices: featurePrices.map((fp) => ({
                capability_id: fp.capability_id,
                name: fp.name,
                pricing_model: fp.pricing_model,
                amount_cents: fp.amount_cents,
                billing_interval: fp.billing_interval,
                ...(fp.pricing_model === 'metered'
                    ? { metric: fp.metric, usage_limit: fp.usage_limit, overage_rate_cents: fp.overage_rate_cents }
                    : {}),
                ...(fp.pricing_model === 'tiered' ? { tiers: fp.tiers } : {}),
            })),
            user_id: identity?.sub ?? '',
        };

        try {
            await api.post('/billing/products/publish', payload);
            toast.success('Product published successfully');
            onProductCreated?.();
            onClose();
        } catch (err) {
            const res = (err as { response?: { status?: number; data?: Record<string, unknown> } })?.response;
            const data = res?.data ?? {};
            const serverErrors: Record<string, string> =
                (data.errors as Record<string, string>) ?? {};
            const serverMessage =
                (data.message as string) ?? (data.error as string);
            if (serverMessage && Object.keys(serverErrors).length === 0) {
                serverErrors.publish = serverMessage;
            }
            logger.error('ProductFormWizard:handlePublish - Failed to publish product', { errors: serverErrors });
            setErrors(serverErrors);
            toast.error(serverMessage || 'Failed to publish product');
        } finally {
            setPublishing(false);
        }
    };

    // -----------------------------------------------------------------------
    // Progress
    // -----------------------------------------------------------------------
    const progressPercent = ((currentStep + 1) / WIZARD_STEPS.length) * 100;
    const isLastStep = currentStep === WIZARD_STEPS.length - 1;

    // -----------------------------------------------------------------------
    // Render
    // -----------------------------------------------------------------------
    return (
        <SlideDrawer isOpen={isOpen} onClose={onClose}>
            <div className="flex h-full flex-col outline-none focus:outline-none" onKeyDown={handleKeyDown}>
                        {/* Header */}
                        <div className="px-8 py-6 border-b border-slate-100 bg-slate-50/50">
                            <div className="flex items-center justify-between mb-3">
                                <div className="flex items-center gap-3">
                                    <div className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-100">
                                        <Package className="h-5 w-5 text-emerald-600" />
                                    </div>
                                    <div>
                                        <h2 className="text-2xl font-semibold text-slate-900">
                                            Publish Product
                                        </h2>
                                        <p className="text-sm text-slate-500">
                                            Step {currentStep + 1} of {WIZARD_STEPS.length}: {WIZARD_STEPS[currentStep].title}
                                        </p>
                                    </div>
                                </div>
                                <button
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
                                    className="rounded-full p-1 hover:bg-slate-200 text-slate-400 hover:text-slate-600 transition-colors outline-none focus:ring-2 focus:ring-slate-900"
                                    aria-label="Close"
                                >
                                    <X size={20} />
                                </button>
                            </div>

                            {/* Step Indicators */}
                            <div className="flex items-center gap-2 mt-2">
                                {WIZARD_STEPS.map((step, index) => (
                                    <div key={step.id} className="flex items-center">
                                        <div
                                            className={cn(
                                                'flex h-8 w-8 items-center justify-center rounded-full text-sm font-medium transition-colors',
                                                index < currentStep
                                                    ? 'bg-emerald-500 text-white'
                                                    : index === currentStep
                                                        ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500'
                                                        : 'bg-slate-100 text-slate-400',
                                            )}
                                        >
                                            {index < currentStep ? <Check size={16} /> : index + 1}
                                        </div>
                                        {index < WIZARD_STEPS.length - 1 && (
                                            <div
                                                className={cn(
                                                    'mx-2 h-0.5 w-12 transition-colors',
                                                    index < currentStep ? 'bg-emerald-500' : 'bg-slate-200',
                                                )}
                                            />
                                        )}
                                    </div>
                                ))}
                            </div>

                            <Progress value={progressPercent} className="mt-4 h-1" />
                        </div>

                        {/* Form Content */}
                        <div className="flex-1 overflow-y-auto p-8 bg-white">
                            <AnimatePresence mode="wait">
                                {/* ====== STEP 1: Select Application ====== */}
                                {currentStep === 0 && (
                                    <motion.div
                                        key="step-application"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="space-y-6 max-w-2xl mx-auto"
                                    >
                                        <div className="space-y-2">
                                            <Label>Application <span className="text-red-500">*</span></Label>
                                            {loadingApps ? (
                                                <div className="space-y-2">
                                                    <Skeleton className="h-9 w-full" />
                                                    <Skeleton className="h-4 w-48" />
                                                </div>
                                            ) : (
                                                <>
                                                    <Select
                                                        value={selectedAppKey || APPLICATION_NONE}
                                                        onValueChange={handleAppChange}
                                                    >
                                                        <SelectTrigger
                                                            className={errors.application ? 'border-red-300 bg-red-50/50' : 'border-slate-200'}
                                                        >
                                                            <SelectValue placeholder="Select an application" />
                                                        </SelectTrigger>
                                                        <SelectContent className="z-[250]" position="popper">
                                                            <SelectItem value={APPLICATION_NONE} disabled className="text-muted-foreground">
                                                                Select an application
                                                            </SelectItem>
                                                            {applications.map((app) => (
                                                                <SelectItem key={app.app_key} value={app.app_key}>
                                                                    {app.display_name || app.name || app.app_key}
                                                                    <span className="ml-2 text-slate-500 font-mono text-xs">
                                                                        ({app.app_key})
                                                                    </span>
                                                                </SelectItem>
                                                            ))}
                                                        </SelectContent>
                                                    </Select>
                                                    <p className="text-xs text-slate-400">
                                                        The application whose capabilities will be offered in this product.
                                                    </p>
                                                </>
                                            )}
                                            {errors.application && (
                                                <p className="text-xs text-red-500 flex items-center gap-1">
                                                    <AlertCircle size={12} />
                                                    {errors.application}
                                                </p>
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Product Title <span className="text-red-500">*</span></Label>
                                            <Input
                                                placeholder="e.g. Premium AI Toolkit"
                                                value={title}
                                                onChange={(e) => setTitle(e.target.value)}
                                                className={errors.title ? errorInputClassName : inputClassName}
                                                autoFocus
                                            />
                                            {errors.title && (
                                                <p className="text-xs text-red-500 flex items-center gap-1">
                                                    <AlertCircle size={12} />
                                                    {errors.title}
                                                </p>
                                            )}
                                        </div>

                                        <div className="space-y-2">
                                            <Label>Summary</Label>
                                            <textarea
                                                placeholder="A short description of what this product offers..."
                                                value={summary}
                                                onChange={(e) => setSummary(e.target.value)}
                                                rows={3}
                                                className={`${inputClassName} resize-none`}
                                            />
                                        </div>
                                    </motion.div>
                                )}

                                {/* ====== STEP 2: Select Capabilities ====== */}
                                {currentStep === 1 && (
                                    <motion.div
                                        key="step-capabilities"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="space-y-5 max-w-3xl mx-auto"
                                    >
                                        {/* Toolbar */}
                                        <div className="flex items-center gap-3">
                                            <div className="relative flex-1">
                                                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                                                <Input
                                                    placeholder="Search capabilities..."
                                                    value={capSearch}
                                                    onChange={(e) => setCapSearch(e.target.value)}
                                                    className={`${inputClassName} pl-9`}
                                                />
                                            </div>
                                            <button
                                                type="button"
                                                onClick={selectAllVisible}
                                                className="text-xs font-medium text-emerald-600 hover:text-emerald-700 whitespace-nowrap"
                                            >
                                                Select All
                                            </button>
                                            <span className="text-slate-300">|</span>
                                            <button
                                                type="button"
                                                onClick={clearSelection}
                                                className="text-xs font-medium text-slate-500 hover:text-slate-700 whitespace-nowrap"
                                            >
                                                Clear
                                            </button>
                                            <Badge variant="secondary" className="ml-auto">
                                                {selectedCapIds.size} selected
                                            </Badge>
                                        </div>

                                        {errors.capabilities && (
                                            <p className="text-xs text-red-500 flex items-center gap-1">
                                                <AlertCircle size={12} />
                                                {errors.capabilities}
                                            </p>
                                        )}

                                        {loadingCaps ? (
                                            <div className="space-y-4">
                                                {[1, 2, 3, 4].map((i) => (
                                                    <div key={i} className="flex items-center gap-3">
                                                        <Skeleton className="h-4 w-4 rounded" />
                                                        <Skeleton className="h-4 w-48" />
                                                    </div>
                                                ))}
                                            </div>
                                        ) : filteredCapabilities.length === 0 ? (
                                            <div className="py-12 text-center">
                                                <Sparkles className="mx-auto h-8 w-8 text-slate-300 mb-3" />
                                                <p className="text-sm text-slate-500">
                                                    {capabilities.length === 0
                                                        ? 'No tools, agents, or workflows found for this application. Pick the application that owns the capability you want to sell.'
                                                        : 'No capabilities match your search.'}
                                                </p>
                                            </div>
                                        ) : (
                                            <div className="space-y-6">
                                                {groupOrder.map((groupType) => {
                                                    const caps = groupedCapabilities[groupType];
                                                    if (!caps?.length) return null;
                                                    const Icon = TYPE_ICONS[groupType] ?? Zap;
                                                    const colorCls = TYPE_COLORS[groupType] ?? 'bg-slate-100 text-slate-600';
                                                    const label = groupType === 'other' ? 'Other' : groupType.charAt(0).toUpperCase() + groupType.slice(1) + 's';

                                                    return (
                                                        <div key={groupType}>
                                                            <div className="flex items-center gap-2 mb-3">
                                                                <div className={cn('flex h-6 w-6 items-center justify-center rounded-md', colorCls)}>
                                                                    <Icon size={14} />
                                                                </div>
                                                                <h3 className="text-sm font-semibold text-slate-700">{label}</h3>
                                                                <Badge variant="outline" className="text-[10px]">{caps.length}</Badge>
                                                            </div>
                                                            <div className="grid gap-2">
                                                                {caps.map((cap) => {
                                                                    const selected = !!cap.id && selectedCapIds.has(cap.id);
                                                                    const selectable = !!cap.id;
                                                                    return (
                                                                        <label
                                                                            key={cap.id || cap.fqcn || cap.name}
                                                                            className={cn(
                                                                                'flex items-start gap-3 rounded-lg border p-3 transition-all',
                                                                                !selectable
                                                                                    ? 'cursor-not-allowed opacity-60 border-slate-200'
                                                                                    : selected
                                                                                    ? 'cursor-pointer border-emerald-300 bg-emerald-50/60 shadow-sm'
                                                                                    : 'cursor-pointer border-slate-200 hover:border-slate-300 hover:bg-slate-50/50',
                                                                            )}
                                                                        >
                                                                            <Checkbox
                                                                                checked={selected}
                                                                                disabled={!selectable}
                                                                                onCheckedChange={() => selectable && toggleCapability(cap.id)}
                                                                                className="mt-0.5"
                                                                            />
                                                                            <div className="flex-1 min-w-0">
                                                                                <div className="flex items-center gap-2">
                                                                                    <span className="text-sm font-medium text-slate-800 truncate">
                                                                                        {cap.name}
                                                                                    </span>
                                                                                    <Badge variant="outline" className="text-[10px] shrink-0">
                                                                                        {cap.type}
                                                                                    </Badge>
                                                                                </div>
                                                                                {cap.description && (
                                                                                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                                                                                        {cap.description}
                                                                                    </p>
                                                                                )}
                                                                            </div>
                                                                        </label>
                                                                    );
                                                                })}
                                                            </div>
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        )}
                                    </motion.div>
                                )}

                                {/* ====== STEP 3: Configure Pricing ====== */}
                                {currentStep === 2 && (
                                    <motion.div
                                        key="step-pricing"
                                        initial={{ opacity: 0, x: 20 }}
                                        animate={{ opacity: 1, x: 0 }}
                                        exit={{ opacity: 0, x: -20 }}
                                        transition={{ duration: 0.2 }}
                                        className="space-y-8 max-w-3xl mx-auto"
                                    >
                                        {/* Publish error */}
                                        {errors.publish && (
                                            <div className="p-3 rounded-lg bg-red-50 border border-red-200 flex items-start gap-2">
                                                <AlertCircle size={16} className="text-red-500 mt-0.5 shrink-0" />
                                                <p className="text-sm text-red-700">{errors.publish}</p>
                                            </div>
                                        )}

                                        {/* Trial Period */}
                                        <section className="space-y-3">
                                            <h3 className="text-base font-semibold text-slate-800">Trial Period</h3>
                                            <div className="flex items-center gap-3">
                                                <Input
                                                    type="number"
                                                    min={0}
                                                    max={365}
                                                    value={trialDays}
                                                    onChange={(e) => setTrialDays(Math.max(0, parseInt(e.target.value) || 0))}
                                                    className={`${inputClassName} w-28`}
                                                />
                                                <span className="text-sm text-slate-500">days free trial</span>
                                            </div>
                                        </section>

                                        {/* Base Price */}
                                        <section className="space-y-3">
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-base font-semibold text-slate-800">Base Price</h3>
                                                <div className="flex items-center gap-2">
                                                    <span className="text-xs text-slate-500">{enableBasePrice ? 'Enabled' : 'Disabled'}</span>
                                                    <Switch
                                                        checked={enableBasePrice}
                                                        onCheckedChange={setEnableBasePrice}
                                                    />
                                                </div>
                                            </div>
                                            {enableBasePrice && (
                                                <motion.div
                                                    initial={{ opacity: 0, height: 0 }}
                                                    animate={{ opacity: 1, height: 'auto' }}
                                                    exit={{ opacity: 0, height: 0 }}
                                                    className="grid grid-cols-3 gap-4"
                                                >
                                                    <div className="space-y-1.5">
                                                        <Label className="text-xs">Amount ($)</Label>
                                                        <div className="relative">
                                                            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                            <Input
                                                                type="number"
                                                                step="0.01"
                                                                min={0}
                                                                placeholder="0.00"
                                                                value={basePrice.amount_cents ? centsToDisplay(basePrice.amount_cents) : ''}
                                                                onChange={(e) => setBasePrice((p) => ({ ...p, amount_cents: displayToCents(e.target.value) }))}
                                                                className={`${inputClassName} pl-8`}
                                                            />
                                                        </div>
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-xs">Currency</Label>
                                                        <Input value="USD" disabled className={`${inputClassName} bg-slate-50`} />
                                                    </div>
                                                    <div className="space-y-1.5">
                                                        <Label className="text-xs">Interval</Label>
                                                        <Select
                                                            value={basePrice.billing_interval}
                                                            onValueChange={(v) => setBasePrice((p) => ({ ...p, billing_interval: v as BillingInterval }))}
                                                        >
                                                            <SelectTrigger className="border-slate-200">
                                                                <SelectValue />
                                                            </SelectTrigger>
                                                            <SelectContent className="z-[250]" position="popper">
                                                                <SelectItem value="month">Monthly</SelectItem>
                                                                <SelectItem value="year">Yearly</SelectItem>
                                                            </SelectContent>
                                                        </Select>
                                                    </div>
                                                </motion.div>
                                            )}
                                        </section>

                                        {/* Per-Capability Pricing */}
                                        <section className="space-y-4">
                                            <h3 className="text-base font-semibold text-slate-800">
                                                Capability Pricing
                                                <span className="ml-2 text-sm font-normal text-slate-500">
                                                    ({featurePrices.length} {featurePrices.length === 1 ? 'feature' : 'features'})
                                                </span>
                                            </h3>

                                            {featurePrices.length === 0 && (
                                                <p className="text-sm text-slate-400 py-4 text-center">
                                                    No capabilities selected. Go back to step 2 to select capabilities.
                                                </p>
                                            )}

                                            <div className="space-y-4">
                                                {featurePrices.map((fp) => {
                                                    const hasError = !!errors[`feature_${fp.capability_id}`];
                                                    return (
                                                        <div
                                                            key={fp.capability_id}
                                                            className={cn(
                                                                'rounded-lg border p-5 space-y-4 transition-colors',
                                                                hasError ? 'border-red-300 bg-red-50/30' : 'border-slate-200 bg-slate-50/30',
                                                            )}
                                                        >
                                                            {/* Card header */}
                                                            <div className="flex items-center gap-3">
                                                                <div className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-100">
                                                                    <Zap size={14} className="text-slate-600" />
                                                                </div>
                                                                <div className="flex-1 min-w-0">
                                                                    <Input
                                                                        value={fp.name}
                                                                        onChange={(e) => updateFeaturePrice(fp.capability_id, { name: e.target.value })}
                                                                        className="text-sm font-semibold border-0 bg-transparent p-0 h-auto shadow-none focus:ring-0 focus:outline-none"
                                                                        placeholder="Feature name"
                                                                    />
                                                                </div>
                                                            </div>

                                                            {/* Pricing model selector */}
                                                            <div className="grid grid-cols-4 gap-2">
                                                                {PRICING_MODELS.map((pm) => (
                                                                    <button
                                                                        key={pm.value}
                                                                        type="button"
                                                                        onClick={() => updateFeaturePrice(fp.capability_id, { pricing_model: pm.value })}
                                                                        className={cn(
                                                                            'rounded-md border px-3 py-2 text-left transition-all',
                                                                            fp.pricing_model === pm.value
                                                                                ? 'border-emerald-400 bg-emerald-50 shadow-sm'
                                                                                : 'border-slate-200 hover:border-slate-300 bg-white',
                                                                        )}
                                                                    >
                                                                        <span className={cn(
                                                                            'block text-xs font-semibold',
                                                                            fp.pricing_model === pm.value ? 'text-emerald-700' : 'text-slate-700',
                                                                        )}>
                                                                            {pm.label}
                                                                        </span>
                                                                        <span className="block text-[10px] text-slate-400 mt-0.5">
                                                                            {pm.description}
                                                                        </span>
                                                                    </button>
                                                                ))}
                                                            </div>

                                                            {/* Flat / Per Unit fields */}
                                                            {(fp.pricing_model === 'flat' || fp.pricing_model === 'per_unit') && (
                                                                <div className="grid grid-cols-2 gap-4">
                                                                    <div className="space-y-1.5">
                                                                        <Label className="text-xs">
                                                                            {fp.pricing_model === 'flat' ? 'Amount ($)' : 'Unit Price ($)'}
                                                                        </Label>
                                                                        <div className="relative">
                                                                            <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                                            <Input
                                                                                type="number"
                                                                                step="0.01"
                                                                                min={0}
                                                                                placeholder="0.00"
                                                                                value={fp.amount_cents ? centsToDisplay(fp.amount_cents) : ''}
                                                                                onChange={(e) =>
                                                                                    updateFeaturePrice(fp.capability_id, { amount_cents: displayToCents(e.target.value) })
                                                                                }
                                                                                className={`${inputClassName} pl-8`}
                                                                            />
                                                                        </div>
                                                                    </div>
                                                                    <div className="space-y-1.5">
                                                                        <Label className="text-xs">Interval</Label>
                                                                        <Select
                                                                            value={fp.billing_interval}
                                                                            onValueChange={(v) =>
                                                                                updateFeaturePrice(fp.capability_id, { billing_interval: v as BillingInterval })
                                                                            }
                                                                        >
                                                                            <SelectTrigger className="border-slate-200">
                                                                                <SelectValue />
                                                                            </SelectTrigger>
                                                                            <SelectContent className="z-[250]" position="popper">
                                                                                <SelectItem value="month">Monthly</SelectItem>
                                                                                <SelectItem value="year">Yearly</SelectItem>
                                                                            </SelectContent>
                                                                        </Select>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Metered fields */}
                                                            {fp.pricing_model === 'metered' && (
                                                                <div className="space-y-4">
                                                                    <div className="grid grid-cols-2 gap-4">
                                                                        <div className="space-y-1.5">
                                                                            <Label className="text-xs">Unit Amount ($)</Label>
                                                                            <div className="relative">
                                                                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                                                <Input
                                                                                    type="number"
                                                                                    step="0.0001"
                                                                                    min={0}
                                                                                    placeholder="0.0000"
                                                                                    value={fp.amount_cents ? centsToDisplay(fp.amount_cents) : ''}
                                                                                    onChange={(e) =>
                                                                                        updateFeaturePrice(fp.capability_id, { amount_cents: displayToCents(e.target.value) })
                                                                                    }
                                                                                    className={`${inputClassName} pl-8`}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                        <div className="space-y-1.5">
                                                                            <Label className="text-xs">Metric</Label>
                                                                            <Select
                                                                                value={fp.metric}
                                                                                onValueChange={(v) =>
                                                                                    updateFeaturePrice(fp.capability_id, { metric: v as MetricType })
                                                                                }
                                                                            >
                                                                                <SelectTrigger className="border-slate-200">
                                                                                    <SelectValue />
                                                                                </SelectTrigger>
                                                                                <SelectContent className="z-[250]" position="popper">
                                                                                    {METRIC_OPTIONS.map((m) => (
                                                                                        <SelectItem key={m.value} value={m.value}>
                                                                                            {m.label}
                                                                                        </SelectItem>
                                                                                    ))}
                                                                                </SelectContent>
                                                                            </Select>
                                                                        </div>
                                                                    </div>
                                                                    <div className="grid grid-cols-2 gap-4">
                                                                        <div className="space-y-1.5">
                                                                            <Label className="text-xs">Usage Limit</Label>
                                                                            <Input
                                                                                type="number"
                                                                                min={0}
                                                                                placeholder="Unlimited"
                                                                                value={fp.usage_limit ?? ''}
                                                                                onChange={(e) =>
                                                                                    updateFeaturePrice(fp.capability_id, {
                                                                                        usage_limit: e.target.value ? parseInt(e.target.value) || 0 : null,
                                                                                    })
                                                                                }
                                                                                className={inputClassName}
                                                                            />
                                                                            <p className="text-[10px] text-slate-400">Leave empty for unlimited</p>
                                                                        </div>
                                                                        <div className="space-y-1.5">
                                                                            <Label className="text-xs">Overage Rate ($)</Label>
                                                                            <div className="relative">
                                                                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                                                <Input
                                                                                    type="number"
                                                                                    step="0.0001"
                                                                                    min={0}
                                                                                    placeholder="0.0000"
                                                                                    value={fp.overage_rate_cents ? centsToDisplay(fp.overage_rate_cents) : ''}
                                                                                    onChange={(e) =>
                                                                                        updateFeaturePrice(fp.capability_id, { overage_rate_cents: displayToCents(e.target.value) })
                                                                                    }
                                                                                    className={`${inputClassName} pl-8`}
                                                                                />
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>
                                                            )}

                                                            {/* Tiered fields */}
                                                            {fp.pricing_model === 'tiered' && (
                                                                <div className="space-y-3">
                                                                    <div className="grid grid-cols-[1fr_1fr_40px] gap-2 items-end text-xs font-medium text-slate-500">
                                                                        <span>Up To (units)</span>
                                                                        <span>Price per Unit ($)</span>
                                                                        <span />
                                                                    </div>
                                                                    {fp.tiers.map((tier, idx) => (
                                                                        <div key={idx} className="grid grid-cols-[1fr_1fr_40px] gap-2 items-center">
                                                                            <Input
                                                                                type="number"
                                                                                min={0}
                                                                                placeholder={idx === fp.tiers.length - 1 ? '∞' : '0'}
                                                                                value={tier.up_to ?? ''}
                                                                                onChange={(e) =>
                                                                                    updateTier(fp.capability_id, idx, {
                                                                                        up_to: e.target.value ? parseInt(e.target.value) || 0 : null,
                                                                                    })
                                                                                }
                                                                                className={inputClassName}
                                                                            />
                                                                            <div className="relative">
                                                                                <DollarSign className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                                                                                <Input
                                                                                    type="number"
                                                                                    step="0.01"
                                                                                    min={0}
                                                                                    placeholder="0.00"
                                                                                    value={tier.unit_amount_cents ? centsToDisplay(tier.unit_amount_cents) : ''}
                                                                                    onChange={(e) =>
                                                                                        updateTier(fp.capability_id, idx, {
                                                                                            unit_amount_cents: displayToCents(e.target.value),
                                                                                        })
                                                                                    }
                                                                                    className={`${inputClassName} pl-8`}
                                                                                />
                                                                            </div>
                                                                            <button
                                                                                type="button"
                                                                                onClick={() => removeTier(fp.capability_id, idx)}
                                                                                disabled={fp.tiers.length <= 1}
                                                                                className="flex h-9 w-9 items-center justify-center rounded-md text-slate-400 hover:text-red-500 hover:bg-red-50 transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
                                                                                aria-label="Remove tier"
                                                                            >
                                                                                <Trash2 size={14} />
                                                                            </button>
                                                                        </div>
                                                                    ))}
                                                                    <button
                                                                        type="button"
                                                                        onClick={() => addTier(fp.capability_id)}
                                                                        className="flex items-center gap-1 text-xs font-medium text-emerald-600 hover:text-emerald-700"
                                                                    >
                                                                        <Plus size={12} />
                                                                        Add Tier
                                                                    </button>
                                                                </div>
                                                            )}

                                                            {hasError && (
                                                                <p className="text-xs text-red-500 flex items-center gap-1">
                                                                    <AlertCircle size={12} />
                                                                    {errors[`feature_${fp.capability_id}`]}
                                                                </p>
                                                            )}
                                                        </div>
                                                    );
                                                })}
                                            </div>
                                        </section>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </div>

                        {/* Footer */}
                        <div className="px-8 py-5 border-t border-slate-100 bg-slate-50/50 flex justify-between items-center">
                            <div>
                                {currentStep > 0 && (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); handleBack(); }}
                                        disabled={publishing}
                                        className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                                    >
                                        <ChevronLeft size={16} />
                                        Back
                                    </button>
                                )}
                            </div>
                            <div className="flex items-center gap-3">
                                <button
                                    type="button"
                                    onClick={(e) => { e.preventDefault(); e.stopPropagation(); onClose(); }}
                                    disabled={publishing}
                                    className="inline-flex items-center justify-center gap-2 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 shadow-sm transition-colors hover:bg-slate-50 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50"
                                >
                                    Cancel
                                </button>
                                {isLastStep ? (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); handlePublish(); }}
                                        disabled={publishing || !canAdvance(currentStep)}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-emerald-600 px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-emerald-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        {publishing ? (
                                            <>
                                                <Loader2 size={16} className="animate-spin" />
                                                Publishing...
                                            </>
                                        ) : (
                                            <>
                                                Publish Product
                                                <Check size={16} />
                                            </>
                                        )}
                                    </button>
                                ) : (
                                    <button
                                        type="button"
                                        onClick={(e) => { e.preventDefault(); handleNext(); }}
                                        disabled={!canAdvance(currentStep)}
                                        className="inline-flex items-center justify-center gap-2 rounded-md bg-btn-primary px-4 py-2 text-sm font-medium text-white shadow-sm transition-colors hover:bg-btn-primary-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                                    >
                                        Next
                                        <ChevronRight size={16} />
                                    </button>
                                )}
                            </div>
                        </div>
            </div>
        </SlideDrawer>
    );
}
